import React from "react";
import type { ViewComponent } from "@frui.ts/views";
import { observer } from "mobx-react-lite";
import type ChangeVehicleViewModel from "viewModels/callCenter/actions/changeVehicleViewModel";
import { formatDate, formatDateTime, formatLicensePlate, translateEnum, translateGeneral } from "utils/localizations";
import { DataTable } from "@frui.ts/dataviews";
import type { ColumnDefinition } from "@frui.ts/dataviews/dist/dataTypes";
import { chargeOrderStatusToVariant } from "entities/chargeOrderStatus";
import { chargeDisplayStatusToVariant } from "entities/chargeDisplayStatus";
import Form from "react-bootstrap/Form";
import Col from "react-bootstrap/Col";
import FormLabel from "../../../../controls/formLabel";
import type VehicleRegistrationChangeChargeResponse from "entities/vehicleRegistrationChangeChargeResponse";
import type VehicleRegistrationChangeExemptionResponse from "entities/vehicleRegistrationChangeExemptionResponse";
import type VehicleRegisterSynchronizationStatus from "entities/vehicleRegisterSynchronizationStatus";
import { vehicleRegisterSynchronizationStatusToVariant } from "entities/vehicleRegisterSynchronizationStatus";
import AlertWithIcon from "views/common/alertWithIcon";
import { emptyValue } from "utils/emptyValue";
import Button from "@edaz/edazkit/Button";

// eslint-disable-next-line sonarjs/cognitive-complexity
const ChangeVehicleSearchResults: ViewComponent<ChangeVehicleViewModel> = observer(({ vm }) => {
  if (!vm.searchPreview) {
    return null;
  }

  const renderVehicleRegisterSynchronizationStatus = (value?: VehicleRegisterSynchronizationStatus) => {
    if (!value) {
      return emptyValue;
    }
    const formattedValue = vm.translateVehicleRegisterSynchronizationStatus(value);
    return <strong className={`text-${vehicleRegisterSynchronizationStatusToVariant(value)}`}>{formattedValue}</strong>;
  };
  const renderValue = (value?: any) => {
    return value ?? emptyValue;
  };

  const textLeftClassName = "text-left align-middle";
  const verticalMiddleClassName = "align-middle";

  const charges = vm.charges;
  const chargesColumns: ColumnDefinition<VehicleRegistrationChangeChargeResponse>[] = [
    {
      property: "licensePlate",
      headerClassName: verticalMiddleClassName,
      title: vm.translateSubAttribute("changeVehicleCharge", "licensePlate"),
      cellProps: _ => ({ style: { textWrap: "nowrap" } }),
      valueFormatter: props => formatLicensePlate({ licensePlate: props.value, countryId: props.item.countryId }),
    },
    {
      title: vm.translateSubAttribute("changeVehicleCharge", "_chargeTypeId"),
      headerClassName: verticalMiddleClassName,
      valueFormatter: props => vm.translateChargeType(props.item.priceListItemId),
    },
    {
      title: vm.translateSubAttribute("changeVehicleCharge", "_fuelTypeId"),
      headerClassName: verticalMiddleClassName,
      valueFormatter: props => vm.translateFuelType(props.item.priceListItemId),
    },
    {
      property: "validSince",
      cellClassName: textLeftClassName,
      headerClassName: textLeftClassName,
      title: vm.translateSubAttribute("changeVehicleCharge", "validSince"),
      cellProps: _ => ({ style: { maxWidth: 100 } }),
      valueFormatter: props => formatDateTime(props.value),
    },
    {
      property: "validUntil",
      cellClassName: textLeftClassName,
      headerClassName: textLeftClassName,
      title: vm.translateSubAttribute("changeVehicleCharge", "validUntil"),
      cellProps: _ => ({ style: { maxWidth: 100 } }),
      valueFormatter: props => formatDateTime(props.value),
    },
    {
      property: "orderIdent",
      cellClassName: textLeftClassName,
      headerClassName: textLeftClassName,
      title: vm.translateSubAttribute("changeVehicleCharge", "orderIdent"),
    },
    {
      property: "chargeOrderPaymentDisplayType",
      headerClassName: verticalMiddleClassName,
      title: vm.translate("purchased"),
      valueFormatter: props => vm.translatePurchased(props.item),
    },
    {
      property: "chargeOrderStatus",
      headerClassName: verticalMiddleClassName,
      title: vm.translateSubAttribute("changeVehicleCharge", "chargeOrderStatus"),
      valueFormatter: props => (
        <span className={`font-weight-bold text-${chargeOrderStatusToVariant(props.value)}`}>
          {translateEnum("chargeOrderStatus", props.value)}
        </span>
      ),
    },
    {
      property: "chargeDisplayStatus",
      headerClassName: verticalMiddleClassName,
      title: vm.translateSubAttribute("changeVehicleCharge", "chargeDisplayStatus"),
      valueFormatter: props => (
        <span className={`font-weight-bold text-${chargeDisplayStatusToVariant(props.value)}`}>
          {translateEnum("chargeDisplayStatus", props.value)}
        </span>
      ),
    },
    {
      property: "emailAddress",
      headerClassName: verticalMiddleClassName,
      title: vm.translateSubAttribute("changeVehicleCharge", "emailAddress"),
      valueFormatter: props => props.value ?? emptyValue,
    },
    {
      property: "vehicleRegisterVehicle",
      headerClassName: verticalMiddleClassName,
      cellProps: _ => ({ style: { width: "170px" } }),
      headerFormatter: props => {
        return (
          <th key={props.key} className="d-flex flex-column" style={{ width: "170px" }}>
            <span>{vm.translate("vin")}</span>
            <span>{vm.translate("externalVehicleIdent")}</span>
          </th>
        );
      },
      valueFormatter: props => (
        <div className="d-flex flex-column">
          <span>{props.item.vehicleRegisterVehicle?.vin ?? emptyValue}</span>
          <span>{props.item.vehicleRegisterVehicle?.externalVehicleIdent ?? emptyValue}</span>
        </div>
      ),
    },
    {
      property: "vehicleRegisterSynchronizationStatus",
      headerClassName: verticalMiddleClassName,
      title: vm.translateSubAttribute("changeVehicleCharge", "vehicleRegisterSynchronizationStatus"),
      cellProps: _ => ({ style: { width: "170px" } }),
      valueFormatter: props => renderVehicleRegisterSynchronizationStatus(props.value),
    },
  ];

  const exemptions = vm.exemptions;
  const exemptionsColumns: ColumnDefinition<VehicleRegistrationChangeExemptionResponse>[] = [
    {
      property: "licensePlate",
      headerClassName: verticalMiddleClassName,
      title: vm.translateSubAttribute("changeVehicleExemption", "licensePlate"),
      cellProps: _ => ({ style: { textWrap: "nowrap" } }),
      valueFormatter: props => formatLicensePlate({ licensePlate: props.value, countryId: props.item.countryId }),
    },
    {
      property: "validSince",
      cellClassName: textLeftClassName,
      headerClassName: textLeftClassName,
      title: vm.translateSubAttribute("changeVehicleExemption", "validSince"),
      cellProps: _ => ({ style: { width: 100 } }),
      valueFormatter: props => formatDateTime(props.value),
    },
    {
      property: "validUntil",
      cellClassName: textLeftClassName,
      headerClassName: textLeftClassName,
      title: vm.translateSubAttribute("changeVehicleExemption", "validUntil"),
      cellProps: _ => ({ style: { width: 100 } }),
      valueFormatter: props => formatDateTime(props.value) || emptyValue,
    },
    {
      property: "creationRecordId",
      headerClassName: verticalMiddleClassName,
      title: vm.translateSubAttribute("changeVehicleExemption", "creationRecordId"),
    },
    {
      property: "cancellationRecordId",
      headerClassName: verticalMiddleClassName,
      title: vm.translateSubAttribute("changeVehicleExemption", "cancellationRecordId"),
      valueFormatter: props => props.value ?? emptyValue,
    },
    {
      property: "exemptionReasonId",
      headerClassName: verticalMiddleClassName,
      title: vm.translateSubAttribute("changeVehicleExemption", "exemptionReasonId"),
      cellClassName: "text-wrap",
      cellProps: props => ({ style: { maxWidth: 400 } }),
      valueFormatter: props => vm.translateExemptionReasonId(props.value),
    },
    {
      property: "vehicleRegisterVehicle",
      headerClassName: verticalMiddleClassName,
      title: vm.translateSubAttribute("changeVehicleExemption", "vehicleRegisterVehicle"),
      cellProps: _ => ({ style: { width: "170px" } }),
      // eslint-disable-next-line sonarjs/no-identical-functions
      headerFormatter: props => {
        return (
          <th key={props.key} className="d-flex flex-column" style={{ width: "170px" }}>
            <span>{vm.translate("vin")}</span>
            <span>{vm.translate("externalVehicleIdent")}</span>
          </th>
        );
      },
      // eslint-disable-next-line sonarjs/no-identical-functions
      valueFormatter: props => (
        <div className="d-flex flex-column">
          <span>{props.item.vehicleRegisterVehicle?.vin ?? emptyValue}</span>
          <span>{props.item.vehicleRegisterVehicle?.externalVehicleIdent ?? emptyValue}</span>
        </div>
      ),
    },
    {
      property: "vehicleRegisterSynchronizationStatus",
      headerClassName: verticalMiddleClassName,
      title: vm.translateSubAttribute("changeVehicleExemption", "vehicleRegisterSynchronizationStatus"),
      cellProps: _ => ({ style: { width: "170px" } }),
      valueFormatter: props => renderVehicleRegisterSynchronizationStatus(props.value),
    },
  ];

  return (
    <>
      <div className="bg-light m-6 px-6 pt-6 pb-3 rounded">
        <Form.Row>
          <Form.Group as={Col} sm={3} xs={4}>
            <FormLabel>{vm.translateAttribute("oldCountryId")}</FormLabel>
            <div>{vm.formatCountry(vm.item.oldCountryId)}</div>
          </Form.Group>
          <Form.Group as={Col} sm={2} xs={4}>
            <FormLabel>{vm.translateAttribute("oldLicensePlate")}</FormLabel>
            <div>{renderValue(vm.item.oldLicensePlate)}</div>
          </Form.Group>
          <Form.Group as={Col} sm={2} xs={4}>
            <FormLabel>{vm.translateAttribute("officialRecordId")}</FormLabel>
            <div>{renderValue(vm.item.officialRecordId)}</div>
          </Form.Group>
          <Form.Group as={Col} sm={2} xs={4}>
            <FormLabel>{vm.translateAttribute("submissionDate")}</FormLabel>
            <div className="text-nowrap">{formatDate(vm.item.submissionDate)}</div>
          </Form.Group>
        </Form.Row>
        <Form.Row>
          <Form.Group as={Col} sm={3} xs={4}>
            <FormLabel>{vm.translatePreviewAttribute("countryId")}</FormLabel>
            <div>{vm.searchPreview?.countryId ? vm.formatCountry(vm.searchPreview.countryId) : emptyValue}</div>
          </Form.Group>
          <Form.Group as={Col} sm={2} xs={4}>
            <FormLabel>{vm.translatePreviewAttribute("licensePlate")}</FormLabel>
            <div>{renderValue(vm.searchPreview?.licensePlate)}</div>
          </Form.Group>
          <Form.Group as={Col} sm={2} xs={4}>
            <FormLabel>{vm.translateVehicleRegisterIdentification("vin")}</FormLabel>
            <div>{renderValue(vm.vehicleRegisterVehicle?.vin)}</div>
          </Form.Group>
          <Form.Group as={Col} sm={2} xs={4}>
            <FormLabel>{vm.translateVehicleRegisterIdentification("externalVehicleIdent")}</FormLabel>
            <div>{renderValue(vm.vehicleRegisterVehicle?.externalVehicleIdent)}</div>
          </Form.Group>
          <Form.Group as={Col} sm={3} xs={4}>
            <FormLabel>{vm.translatePreviewAttribute("newVehicleRegisterSynchronizationStatus")}</FormLabel>
            <div>{renderVehicleRegisterSynchronizationStatus(vm.searchPreview?.newVehicleRegisterSynchronizationStatus)}</div>
          </Form.Group>
        </Form.Row>
      </div>
      {charges.length > 0 && (
        <div className="m-6" style={{ minWidth: "auto" }}>
          <div className="bg-secondary px-6 py-3 font-weight-bold text-white">{vm.translate("charges")}</div>
          <DataTable columns={chargesColumns} items={charges} itemKey="chargeId" className="mb-0 table table-striped" />
        </div>
      )}
      {exemptions.length > 0 && (
        <div className="m-6" style={{ minWidth: "auto" }}>
          <div className="bg-secondary px-6 py-3 font-weight-bold text-white">{vm.translate("exemptions")}</div>
          <DataTable columns={exemptionsColumns} items={exemptions} itemKey="exemptionId" className="mb-0 table table-striped" />
        </div>
      )}
      {vm.rejectedReason && (
        <div className="m-6">
          <AlertWithIcon variant="danger">{vm.rejectedReasonByPassing}</AlertWithIcon>
        </div>
      )}
      {vm.searchPreview?.warningReasons?.map((reason, index) => (
        <div className="mt-6 mr-6 ml-6" key={index}>
          <AlertWithIcon key={reason} variant="warning">
            {vm.translate(`warningReason.${reason}`).replace("%LICENSE_PLATE%", vm.searchPreview?.licensePlate ?? emptyValue)}
          </AlertWithIcon>
        </div>
      ))}
      <div className="d-flex justify-content-center reset-focus" style={{ padding: "1rem 0" }}>
        <Button type="reset" onClick={vm.onBackClick} variant="light" style={{ minWidth: "13.5rem" }}>
          {translateGeneral("back")}
        </Button>
        <Button
          type="submit"
          iconLeft={undefined}
          onClick={vm.onSubmit}
          style={{ minWidth: "13.5rem" }}
          disabled={vm.noChargesNoExemptionsWarning || (vm.rejectedReason && !vm.canBeSubmitted)}>
          {translateGeneral("confirm")}
        </Button>
      </div>
    </>
  );
});

export default ChangeVehicleSearchResults;
